import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarIcon, ChevronLeft, ChevronRight, ListChecks, MoveRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, errText } from "@/lib/api";
import { TodoPanel, TodoRow } from "@/components/TodoPanel";
import { EdgeNav } from "@/components/EdgeNav";
import { addDays, isoDate, longDate, parseISO, shortDate } from "@/lib/eventMeta";
import { useAuth } from "@/context/AuthContext";

export default function Todos() {
  const { canWrite, workspace } = useAuth();
  const [params, setParams] = useSearchParams();
  const [date, setDate] = useState(
    params.get("date") ? params.get("date") : isoDate(new Date()),
  );
  const [carry, setCarry] = useState([]);
  const [picked, setPicked] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const rolloverFor = params.get("rollover");

  const loadCarry = useCallback(async () => {
    try {
      const { data } = await api.get("/todos/carry-over", { params: { date } });
      setCarry(data);
      setPicked(data.map((t) => t.todo_id));
    } catch (e) {
      toast.error(errText(e));
    }
  }, [date, workspace?.workspace_id, reloadKey]);

  useEffect(() => {
    loadCarry();
  }, [loadCarry]);

  useEffect(() => {
    setParams(rolloverFor ? { date, rollover: rolloverFor } : { date }, { replace: true });
  }, [date, rolloverFor, setParams]);

  const rollOver = async (fromDate, ids) => {
    try {
      const { data } = await api.post("/todos/rollover", {
        from_date: fromDate,
        to_date: date,
        todo_ids: ids,
      });
      toast.success(`${data.moved} to-do(s) moved to ${shortDate(data.to)}`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const promptDay = rolloverFor && rolloverFor !== date ? rolloverFor : null;
  const promptItems = promptDay ? carry.filter((t) => t.date === promptDay) : [];

  return (
    <div className="space-y-8" data-testid="todos-page">
      <EdgeNav
        onPrev={() => setDate(isoDate(addDays(parseISO(date), -1)))}
        onNext={() => setDate(isoDate(addDays(parseISO(date), 1)))}
        label="day"
      />

      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="label-caps inline-flex items-center gap-2">
            <ListChecks className="h-4 w-4" /> To-Do
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-2">{longDate(date)}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="todos-prev-day"
            onClick={() => setDate(isoDate(addDays(parseISO(date), -1)))}
            className="h-12 w-12 grid place-items-center border border-border rounded-sm hover:bg-accent transition-colors duration-200"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            data-testid="todos-next-day"
            onClick={() => setDate(isoDate(addDays(parseISO(date), 1)))}
            className="h-12 w-12 grid place-items-center border border-border rounded-sm hover:bg-accent transition-colors duration-200"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <Popover>
            <PopoverTrigger asChild>
              <Button data-testid="todos-date-picker" className="rounded-full h-12 px-6 text-base">
                <CalendarIcon className="h-5 w-5 mr-2" /> Pick a date
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0">
              <Calendar
                mode="single"
                selected={parseISO(date)}
                onSelect={(d) => d && setDate(isoDate(d))}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <Button
            variant="outline"
            data-testid="todos-today-btn"
            onClick={() => setDate(isoDate(new Date()))}
            className="rounded-full h-12 px-5"
          >
            Today
          </Button>
        </div>
      </div>

      {promptDay && promptItems.length && canWrite ? (
        <section
          className="surface p-6"
          data-testid="rollover-prompt"
          style={{ borderLeft: "6px solid rgb(var(--c-fixed))" }}
        >
          <p className="label-caps">End of day</p>
          <h2 className="text-base md:text-lg font-heading font-semibold mt-2">
            {promptItems.length} to-do(s) from {shortDate(promptDay)} are still open — move them to{" "}
            {shortDate(date)}?
          </h2>
          <div className="mt-4 space-y-2">
            {promptItems.map((item) => (
              <label
                key={item.todo_id}
                data-testid={`rollover-item-${item.todo_id}`}
                className="flex items-center gap-3 border border-border rounded-sm px-3 py-2.5 cursor-pointer"
              >
                <Checkbox
                  data-testid={`rollover-check-${item.todo_id}`}
                  checked={picked.includes(item.todo_id)}
                  onCheckedChange={() =>
                    setPicked((list) =>
                      list.includes(item.todo_id)
                        ? list.filter((x) => x !== item.todo_id)
                        : [...list, item.todo_id],
                    )
                  }
                />
                <span className="text-base font-medium">{item.title}</span>
              </label>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              data-testid="rollover-confirm"
              onClick={() =>
                rollOver(
                  promptDay,
                  picked.filter((id) => promptItems.some((t) => t.todo_id === id)),
                )
              }
              className="rounded-full h-12 px-6"
            >
              <MoveRight className="h-5 w-5 mr-2" /> Move selected to {shortDate(date)}
            </Button>
            <Button
              variant="outline"
              data-testid="rollover-dismiss"
              onClick={() => setParams({ date }, { replace: true })}
              className="rounded-full h-12 px-5"
            >
              Leave them
            </Button>
          </div>
        </section>
      ) : null}

      <TodoPanel key={`${date}-${reloadKey}`} date={date} />

      {carry.length ? (
        <section className="surface p-6" data-testid="carry-over-panel">
          <p className="label-caps">Unfinished from earlier days</p>
          <div className="mt-4 space-y-2">
            {carry.map((item) => (
              <div key={item.todo_id} className="space-y-1">
                <p className="text-xs text-muted-foreground">{shortDate(item.date)}</p>
                <TodoRow
                  item={item}
                  day={date}
                  canWrite={canWrite}
                  onToggle={async (i) => {
                    await api.post(`/todos/${i.todo_id}/toggle`);
                    setReloadKey((k) => k + 1);
                  }}
                  onDelete={async (i) => {
                    await api.delete(`/todos/${i.todo_id}`);
                    setReloadKey((k) => k + 1);
                  }}
                />
              </div>
            ))}
          </div>
          {canWrite ? (
            <Button
              data-testid="carry-over-move-all"
              onClick={async () => {
                const days = [...new Set(carry.map((t) => t.date))];
                for (const day of days) {
                  await api.post("/todos/rollover", { from_date: day, to_date: date });
                }
                toast.success(`Moved everything to ${shortDate(date)}`);
                setReloadKey((k) => k + 1);
              }}
              className="mt-4 rounded-full h-12 px-6"
            >
              <MoveRight className="h-5 w-5 mr-2" /> Move all to {shortDate(date)}
            </Button>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
